import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  getApiKeys, getApiKeyLimits, createApiKey, rotateApiKey, revokeApiKey,
} from '../../../services/apiKeyService';
import { extractListData, formatDateTime } from '../../../utils/Utils';
import { moduleName } from '../../../utils/moduleNames';

// Public API — API anahtarı yönetimi (Plan 01, Görev 8; tasarım belgesi §4.6, §8).
//
// Tam anahtar (`plainKey`) YALNIZ oluşturma/yenileme yanıtında bir kez gelir. Bu kanca onu
// HİÇBİR state'te tutmaz: create/rotate yanıtı çağırana döner, liste her zaman liste ucundan
// (maskeli) yeniden çekilir. Gösterimin ömrü çağıranın "bir kez göster" çekmecesiyle sınırlıdır.

// Backend `apiKey.nameTooLong` sınırı — input da aynı sınırla kesilir.
export const API_KEY_NAME_MAX = 200;

// Anahtar okuma ve YAZMA MODÜLLERİNDE yazma izni taşıyabilir (yazma fazı 2026-10-13; backend
// PublicWriteModules). Yazma izni okumayı kapsar.
// AT ve PM'in REST kaynağı yoktur: izinleri yalnız o modüllerin olaylarını olay günlüğünden
// (GET /public/v1/events — backend PublicEventLog.ReadScope: ats.* → AT.Read, pm.* → PM.Read)
// okumayı açar. Eskiden listede yoktular ve arayüzden üretilen anahtar 68 olayın 52'sini okuyamıyordu.
// AC.Read şimdiden listededir: GET /gates Plan 04'te gelir; anahtar üretimi modül kataloğu + aboneliğe baktığı için
// bugün de geçerli bir izindir.
// AM (varlık/zimmet) 2026-10-19'da eklendi: binlerce zimmetin başka sistemden API ile aktarımı (vMind).
export const API_KEY_WRITE_MODULES = ['EM', 'LM', 'AC', 'AM'];   // backend PublicWriteModules ile AYNI

export const API_KEY_PERMISSIONS = [
  'EM.Read', 'LM.Read', 'EX.Read', 'TA.Read', 'SM.Read', 'AM.Read', 'AC.Read', 'AT.Read', 'PM.Read', 'PA.Read',
  'EM.Write', 'LM.Write', 'AC.Write', 'AM.Write',
];

// Yalnız olay günlüğünü açan izinler — etikette "Yalnız Olaylar" ile ayrılır, alanın altında açıklanır.
export const EVENTS_ONLY_PERMISSIONS = ['AT.Read', 'PM.Read'];

// EM varsayılan modüldür: TenantModule kaydı olmadan her kiracıda vardır.
const ALWAYS_AVAILABLE_MODULES = ['EM'];

const moduleOf = (permission) => String(permission || '').split('.')[0];

// Kullanıcı bu izni anahtara verebilir mi? YETKİ YÜKSELTME KAPISI: kullanıcı anahtara
// YALNIZ kendi taşıdığı izni verebilir (sunucu: istenen her "XX.Read"/"XX.Write" için
// çağıranda aynı claim, "*" ya da superAdmin; yoksa 403 apiKey.permissionNotHeld).
// AuthContext.canRead/canWrite superAdmin/enterpriseAdmin'de true döner, aksi halde kullanıcının
// ayrıcalık listesine bakar — sunucu kuralıyla birebir. ".Write" yalnız yazma modüllerinde geçerlidir.
// İlgili kapı fonksiyonu verilmezse izin YOK sayılır (kapalı kapı).
export function userHoldsPermission(permission, canRead, canWrite) {
  const match = /^([A-Z]{2})\.(Read|Write)$/.exec(permission || '');
  if (!match) return false;
  const check = match[2] === 'Write' ? canWrite : canRead;
  if (match[2] === 'Write' && !API_KEY_WRITE_MODULES.includes(match[1])) return false;
  return typeof check === 'function' && Boolean(check(match[1]));
}

// Anahtarın, kullanıcının TAŞIMADIĞI izinleri. Boş değilse kullanıcı o anahtarı yenileyemez
// (rotasyon eski anahtarın izinlerini yeni anahtara taşır; sunucu da 403 döner).
export function unheldPermissions(permissions, canRead, canWrite) {
  return (Array.isArray(permissions) ? permissions : []).filter((p) => !userHoldsPermission(p, canRead, canWrite));
}

// Seçenekler iki kapıdan geçer:
//  1) ABONELİK: kiracıda aktif olmayan modül gösterilmez — seçilse bile anahtar o modülden
//     hiçbir şey okuyamazdı (sunucu: 400 apiKey.moduleNotSubscribed). EM varsayılan modüldür.
//  2) YETKİ: kullanıcının kendisinin taşımadığı izin gösterilmez (bkz. userHoldsPermission).
export function availablePermissions(hasModule, canRead, canWrite) {
  return API_KEY_PERMISSIONS.filter((permission) => {
    if (!userHoldsPermission(permission, canRead, canWrite)) return false;
    const code = moduleOf(permission);
    if (ALWAYS_AVAILABLE_MODULES.includes(code)) return true;
    return typeof hasModule === 'function' ? Boolean(hasModule(code)) : true;
  });
}

// "EM.Read" → "Çalışan Yönetimi"; "EM.Write" → "Çalışan Yönetimi (Yazma)". Modül adları sol menünün
// grup etiketlerinden gelir (utils/moduleNames.js) — yeni çeviri ailesi açılmaz. Yalnız olay açan izin
// (AT, PM) etikette "(Yalnız Olaylar)" taşır. Beklenmeyen biçimde ham değer döner.
export function permissionLabel(t, permission) {
  const match = /^([A-Z]{2})\.(Read|Write)$/.exec(permission || '');
  if (!match) return permission;
  const name = moduleName(t, match[1]);
  if (match[2] === 'Write') return `${name} (${t('apiKeys.writeAccess')})`;
  return EVENTS_ONLY_PERMISSIONS.includes(permission) ? `${name} (${t('apiKeys.eventsOnly')})` : name;
}

// Yazma izni verilebilen modüllerin adları — alanın altındaki not metninde kullanılır.
export function writeModulesLabel(t) {
  return API_KEY_WRITE_MODULES.map((code) => moduleName(t, code)).join(', ');
}

// Seçenekler arasında yalnız olay açan bir izin var mı (alanın altındaki açıklama yalnız o zaman görünür).
export function hasEventsOnlyPermission(permissions) {
  return (Array.isArray(permissions) ? permissions : []).some((p) => EVENTS_ONLY_PERMISSIONS.includes(p));
}

// GenericFormDrawer'ın asyncSelect alanı YALNIZ onSearch/onGetById okur (asyncConfig ölü alan).
// Seçenekler statik; boş sorgu tüm listeyi döndürür (açılışta boş sorgu atılır).
export function buildPermissionAsync(t, permissions) {
  const options = (permissions || []).map((p) => ({ id: p, name: permissionLabel(t, p) }));
  return {
    onSearch: async (term) => {
      if (!term) return options;
      const needle = term.toLocaleLowerCase('tr');
      return options.filter((o) => o.name.toLocaleLowerCase('tr').includes(needle)
        || o.id.toLocaleLowerCase('tr').includes(needle));
    },
    onGetById: async (id) => options.find((o) => o.id === id) || { id, name: permissionLabel(t, id) },
  };
}

// revokedAt dolu → iptal; expiresAt geçmişte (ya da tam şimdi) → süresi dolmuş; aksi → aktif.
export function apiKeyStatus(row, now = new Date()) {
  if (row?.revokedAt) return 'revoked';
  if (row?.expiresAt) {
    const expires = new Date(row.expiresAt).getTime();
    if (!Number.isNaN(expires) && expires <= now.getTime()) return 'expired';
  }
  return 'active';
}

// SINIR MODELİ (backend ApiKeyDataAccess dosya başı) — canlı anahtar iki türdür:
//   'primary'    = aktif + bitişi YOK (süresiz)
//   'transition' = aktif + bitişi gelecekte (yenilenmiş, 30 gün daha çalışan eski anahtar)
// İptal edilmiş/süresi dolmuş anahtar hiçbir sayaca girmez.
export function apiKeyPhase(row, now = new Date()) {
  const status = apiKeyStatus(row, now);
  if (status !== 'active') return status;
  return row?.expiresAt ? 'transition' : 'primary';
}

// Sunucunun /limits yanıtı → { maxApiKeys, primaryCount, transitionCount } ya da null.
// Biçim tutmazsa null: istemci kapısı devreden çıkar, karar sunucuya kalır (kapı yalnız
// kullanıcıyı boşuna denemekten korur; sınırın asıl bekçisi sunucudur).
export function toApiKeyLimits(res) {
  const d = res?.data?.status ? res.data.data : null;
  if (!d || Array.isArray(d)) return null;
  const nums = [d.maxApiKeys, d.primaryCount, d.transitionCount];
  if (!nums.every((n) => Number.isInteger(n) && n >= 0)) return null;
  return { maxApiKeys: d.maxApiKeys, primaryCount: d.primaryCount, transitionCount: d.transitionCount };
}

// Bu anahtarın YENİLENMESİ sınıra takılıyor mu? Hangi sayaç doluysa onu döndürür:
//  - birincil anahtarın yenilenmesi birincil sayısını DEĞİŞTİRMEZ (eski anahtar geçişe girer),
//    yalnız bir geçiş yuvası harcar → geçişteki sayısı sınırdaysa 'transition';
//  - zaten geçişte olan anahtarın yenilenmesi yeni bir BİRİNCİL ekler → birincil sayısı
//    sınırdaysa 'primary'.
// Böylece süresiz anahtarları sınırda olan kiracı da kesintisiz yenileyebilir.
// Aktif olmayan anahtar ya da bilinmeyen sınır → null (eylem zaten yok / karar sunucuda).
export function rotationLimitBlock(row, limits, now = new Date()) {
  if (!limits) return null;
  const phase = apiKeyPhase(row, now);
  if (phase === 'primary') return limits.transitionCount >= limits.maxApiKeys ? 'transition' : null;
  if (phase === 'transition') return limits.primaryCount >= limits.maxApiKeys ? 'primary' : null;
  return null;
}

export const API_KEY_STATUS_COLORS = {
  active: { color: '#16A34A', bg: '#DCFCE7' },
  expired: { color: '#64748B', bg: '#F1F5F9' },
  revoked: { color: '#E53935', bg: '#FEF2F2' },
};

// Savunma: liste satırı hiçbir koşulda tam anahtar taşımaz — sunucu yanlışlıkla döndürse bile.
// eslint-disable-next-line no-unused-vars
const toListRow = ({ plainKey, ...rest }) => rest;

// Oluşturma/yenileme yanıtı. 401'de interceptor mutasyonu da boş yanıtla ÇÖZER (reject
// etmez); tam anahtar gelmediyse başarı sayılmaz.
const toCreated = (res) => {
  const created = res?.data?.data;
  return res?.data?.status && created?.plainKey ? created : null;
};

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

const KeyText = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor};
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const PermissionChip = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #E0E4E8;
  background: rgba(2, 145, 239, 0.06);
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

const useApiKeysApi = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState([]);
  // { maxApiKeys, primaryCount, transitionCount } ya da null (yüklenemedi → istemci kapısı yok).
  const [limits, setLimits] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setFetching(true);
    // Liste ve sınır AYNI turda çekilir: oluşturma/yenileme/iptal sonrası ikisi birlikte
    // tazelenir, "Yenile" kapısı eski sayaçla karar vermez.
    // Sınır okunamazsa liste YİNE gösterilir (kapı devreden çıkar, karar sunucuda).
    Promise.all([getApiKeys(), Promise.resolve(getApiKeyLimits()).catch(() => null)])
      .then(([res, limitsRes]) => {
        if (seq !== requestSeq.current) return;   // eski isteğin geç gelen yanıtı
        setLimits(toApiKeyLimits(limitsRes));
        // api.js GET okumalarında reject ETMEZ — gerçek sinyal res.data.status.
        // "Yüklenemedi" ile "hiç anahtar yok" AYRI durumlardır.
        if (!res?.data?.status) {
          setFailed(true);
          setData([]);
          return;
        }
        setFailed(false);
        setData(extractListData(res).map(toListRow));
      })
      .finally(() => { if (seq === requestSeq.current) setFetching(false); });
  }, [refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Oluşturma/yenileme yanıtı ÇAĞIRANA döner (tam anahtar dahil) — burada saklanmaz.
  const create = useCallback(async (payload) => {
    try {
      const created = toCreated(await createApiKey(payload));
      if (created) refresh();
      return created;
    } catch {
      return null;   // hata toast'ı interceptor'da
    }
  }, [refresh]);

  const rotate = useCallback(async (id) => {
    try {
      const created = toCreated(await rotateApiKey(id));
      if (created) refresh();
      return created;
    } catch {
      return null;
    }
  }, [refresh]);

  const revoke = useCallback(async (id) => {
    try {
      const res = await revokeApiKey(id);
      const ok = Boolean(res?.data?.status);
      if (ok) refresh();
      return ok;
    } catch {
      return false;
    }
  }, [refresh]);

  // Liste küçük (kiracı başına en fazla 2×sınır canlı anahtar + iptal edilenler) ve sayfalanmaz;
  // kolonlar kendi süzgecini/sıralamasını taşımaz.
  const columns = useMemo(() => [
    {
      name: 'name',
      label: t('apiKeys.name'),
      options: { filter: false, sort: false },
    },
    {
      name: 'maskedKey',
      label: t('apiKeys.maskedKey'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? <KeyText data-role="masked-key">{v}</KeyText> : '-'),
      },
    },
    {
      name: 'permissions',
      label: t('apiKeys.permissions'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (
          <ChipRow>
            {(Array.isArray(v) ? v : []).map((p) => (
              <PermissionChip key={p} data-role="permission-chip">{permissionLabel(t, p)}</PermissionChip>
            ))}
          </ChipRow>
        ),
      },
    },
    {
      name: 'lastUsedAt',
      label: t('apiKeys.lastUsed'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? formatDateTime(v, lang) : t('apiKeys.neverUsed')),
      },
    },
    {
      name: 'expiresAt',
      label: t('apiKeys.expiresAt'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          if (v) return formatDateTime(v, lang);
          // İptal edilmiş anahtarda "Süresiz" yanıltıcı olurdu — durum kolonu zaten söylüyor.
          return data[tableMeta.rowIndex]?.revokedAt ? '-' : t('apiKeys.noExpiry');
        },
      },
    },
    {
      name: 'status',
      label: t('apiKeys.status'),
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const status = apiKeyStatus(row);
          const cfg = API_KEY_STATUS_COLORS[status];
          let label = t('apiKeys.active');
          if (status === 'revoked') label = t('apiKeys.revoked');
          else if (status === 'expired') label = t('apiKeys.expired');
          return (
            <Badge data-role="api-key-status" data-status={status} $color={cfg.color} $bg={cfg.bg}>
              {label}
            </Badge>
          );
        },
      },
    },
  ], [t, lang, data]);

  return {
    fetching, failed, data, limits, columns, refresh, create, rotate, revoke,
  };
};

export default useApiKeysApi;
